import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { Button } from "@/components/ui/button";
import { List, X, CaretDown, SignOut, Bell } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

const scrollHomeToQuery = (navigate, currentPath) => {
  const doScroll = () => {
    const el = document.getElementById("query");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        const ta = el.querySelector("textarea");
        if (ta) ta.focus();
      }, 500);
    }
  };
  if (currentPath === "/") {
    doScroll();
  } else {
    navigate("/?scroll=query");
    setTimeout(doScroll, 400);
  }
};

const NavLink = ({ to, testid, active, children }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`px-3 py-2 text-sm font-medium tracking-tight transition-colors ${
      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </Link>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [notifOpen, setNotifOpen] = useState(false);

  const isActive = (p) => location.pathname === p;

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (mounted) setNotif(data);
      } catch (e) {
        console.warn("[Navbar] notifications fetch failed:", e);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [user]);

  const publicLinks = (
    <>
      <NavLink to="/how-it-works" testid={TID.nav.howItWorks} active={isActive("/how-it-works")}>How It Works</NavLink>
      <NavLink to="/why-choose-us" testid="nav-why-choose" active={isActive("/why-choose-us")}>Why ExactReach</NavLink>
      <NavLink to="/pricing" testid="nav-pricing" active={isActive("/pricing")}>Pricing</NavLink>
      <NavLink to="/faq" testid={TID.nav.faq} active={isActive("/faq")}>FAQ</NavLink>
      <NavLink to="/about" testid="nav-about" active={isActive("/about")}>About</NavLink>
      <NavLink to="/contact" testid={TID.nav.contact} active={isActive("/contact")}>Contact</NavLink>
    </>
  );

  const customerLinks = (
    <>
      <NavLink to="/dashboard" testid={TID.nav.dashboard} active={isActive("/dashboard")}>Dashboard</NavLink>
      <NavLink to="/new-request" testid={TID.nav.newRequest} active={isActive("/new-request")}>New Request</NavLink>
      <NavLink to="/requests" testid={TID.nav.requests} active={isActive("/requests")}>Requests</NavLink>
      <NavLink to="/orders" testid={TID.nav.orders} active={isActive("/orders")}>Orders</NavLink>
      <NavLink to="/icps" testid="nav-icps" active={isActive("/icps")}>Saved ICPs</NavLink>
      <NavLink to="/team" testid="nav-team" active={isActive("/team")}>Team</NavLink>
      <NavLink to="/support" testid={TID.nav.support} active={isActive("/support")}>Support</NavLink>
    </>
  );

  const staffLinks = (
    <>
      <NavLink to="/admin" testid={TID.nav.adminDashboard} active={isActive("/admin")}>Ops</NavLink>
      <NavLink to="/admin/requests" testid={TID.nav.adminRequests} active={isActive("/admin/requests")}>Requests</NavLink>
      <NavLink to="/admin/orders" testid={TID.nav.adminOrders} active={isActive("/admin/orders")}>Orders</NavLink>
      {user?.role === "admin" && <NavLink to="/admin/users" testid={TID.nav.adminUsers} active={isActive("/admin/users")}>Users</NavLink>}
      {user?.role === "admin" && <NavLink to="/admin/settings" testid={TID.nav.adminSettings} active={isActive("/admin/settings")}>Settings</NavLink>}
      <NavLink to="/admin/analytics" testid="nav-admin-analytics" active={isActive("/admin/analytics")}>Analytics</NavLink>
      {user?.role === "admin" && <NavLink to="/admin/discounts" testid="nav-admin-discounts" active={isActive("/admin/discounts")}>Discounts</NavLink>}
      {user?.role === "admin" && <NavLink to="/admin/templates" testid="nav-admin-templates" active={isActive("/admin/templates")}>Templates</NavLink>}
    </>
  );

  const isStaff = user && ["admin", "analyst", "quality", "finance"].includes(user.role);

  return (
    <>
    <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center h-16">
        <Link to={user ? (isStaff ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-2 mr-8" data-testid="logo-link">
          <div className="h-8 w-8 bg-foreground grid place-items-center rounded-sm">
            <span className="text-background font-heading font-extrabold text-sm">ER</span>
          </div>
          <span className="font-heading font-bold tracking-tight text-xl">ExactReach</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1">
          {!user && publicLinks}
          {user && !isStaff && customerLinks}
          {user && isStaff && staffLinks}
        </nav>

        <div className="hidden md:flex items-center gap-3 ml-auto">
          {user && (
            <div className="relative">
              <button
                data-testid="nav-notifications"
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 hover:bg-muted rounded-sm"
              >
                <Bell size={18} />
                {notif.unread > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 bg-primary rounded-full" />
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-border shadow-xl">
                  <div className="p-3 border-b border-border flex items-center justify-between">
                    <span className="font-semibold text-sm">Notifications</span>
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={async () => { await api.post("/notifications/read-all"); setNotif({ ...notif, unread: 0 }); }}
                      data-testid="nav-notif-read-all"
                    >
                      Mark all read
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notif.items.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No notifications</div>}
                    {notif.items.slice(0, 15).map((n) => (
                      <button
                        key={n.notification_id}
                        className="w-full text-left px-4 py-3 border-b border-border hover:bg-muted"
                        onClick={() => { if (n.link) navigate(n.link); setNotifOpen(false); }}
                      >
                        <div className="text-sm font-medium">{n.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {!user && (
            <>
              <Link to="/login" data-testid={TID.nav.login} className="text-sm font-medium hover:underline">Login</Link>
              <Button
                data-testid={TID.nav.check}
                size="sm"
                className="rounded-sm"
                onClick={() => scrollHomeToQuery(navigate, location.pathname)}
              >
                Check My Audience
              </Button>
            </>
          )}
          {user && (
            <>
              <Link to="/account" data-testid={TID.nav.account} className="flex items-center gap-2 text-sm hover:underline">
                {user.picture && <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />}
                <span className="hidden lg:inline">{user.name}</span>
              </Link>
              <button data-testid={TID.nav.logout} onClick={logout} className="p-2 hover:bg-muted rounded-sm" title="Logout">
                <SignOut size={16} />
              </button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden ml-auto" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-white">
          <div className="flex flex-col p-4 gap-2">
            {!user && publicLinks}
            {user && !isStaff && customerLinks}
            {user && isStaff && staffLinks}
            {!user && <Link to="/login" data-testid="nav-login-mobile" className="text-sm">Login</Link>}
            {user && <button data-testid="nav-logout-mobile" onClick={logout} className="text-sm text-left">Logout</button>}
          </div>
        </div>
      )}
    </header>
    <VerifyEmailBanner />
    </>
  );
}
